// Face ID / Touch ID unlock via WebAuthn PRF: the authenticator's secret wraps the passcode-derived key
import { get, set, del } from "idb-keyval";
import { b64, unb64 } from "./secureStore";

const BIO = "hb-bio";
const enc = new TextEncoder();
const PRF_SALT = enc.encode("household-budget-bio-v1");

export const bioRecord = () => get(BIO);
export const disableBio = () => del(BIO);

export async function bioAvailable() {
  if (!window.PublicKeyCredential || !navigator.credentials || !window.isSecureContext) return false;
  try {
    if (!(await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable())) return false;
    if (PublicKeyCredential.getClientCapabilities) {
      const caps = await PublicKeyCredential.getClientCapabilities();
      if (caps && caps["extension:prf"] === false) return false;
    }
    return true;
  } catch {
    return false;
  }
}

async function prfKey(credId) {
  const cred = await navigator.credentials.get({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      allowCredentials: [{ type: "public-key", id: credId }],
      userVerification: "required",
      timeout: 60000,
      extensions: { prf: { eval: { first: PRF_SALT } } },
    },
  });
  const out = cred?.getClientExtensionResults?.().prf?.results?.first;
  if (!out) throw new Error("unsupported");
  return crypto.subtle.importKey("raw", out, "AES-GCM", false, ["encrypt", "decrypt"]);
}

// rawKey: 32 bytes of the passcode-derived AES key (PBKDF2 bits)
export async function enableBio(rawKey) {
  const cred = await navigator.credentials.create({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      rp: { name: "Household Budget" },
      user: { id: crypto.getRandomValues(new Uint8Array(16)), name: "household-budget", displayName: "Household Budget" },
      pubKeyCredParams: [{ type: "public-key", alg: -7 }, { type: "public-key", alg: -257 }],
      authenticatorSelection: { authenticatorAttachment: "platform", residentKey: "preferred", userVerification: "required" },
      timeout: 60000,
      extensions: { prf: {} },
    },
  });
  if (cred.getClientExtensionResults?.().prf?.enabled === false) throw new Error("unsupported");
  const credId = new Uint8Array(cred.rawId);
  const key = await prfKey(credId);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const wrapped = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, rawKey);
  await set(BIO, { credId: b64(credId), iv: b64(iv), wrapped: b64(wrapped), createdAt: Date.now() });
}

// Returns the raw passcode-derived key bytes
export async function unlockBio() {
  const rec = await get(BIO);
  if (!rec) throw new Error("not set");
  const key = await prfKey(unb64(rec.credId));
  return crypto.subtle.decrypt({ name: "AES-GCM", iv: unb64(rec.iv) }, key, unb64(rec.wrapped));
}
